import React from 'react';
import styled from 'styled-components';
import { Select, Spin, Tabs, message } from 'antd';
import TemplateList from 'src/components/views/admin/checklists/TemplateList';
import TemplateEditor from 'src/components/views/admin/checklists/TemplateEditor';
import TodayView from 'src/components/views/admin/checklists/TodayView';
import RunView from 'src/components/views/admin/checklists/RunView';
import HistoryView from 'src/components/views/admin/checklists/HistoryView';
import TeamView from 'src/components/views/admin/checklists/TeamView';
import { localDate } from 'src/redux/actions/checklists';
import { resolveSelectedSite } from 'src/utils/selectedSite';
import { DEFAULT_DEPARTMENTS, blankTemplate } from 'src/constants/checklists';
import { isOversight } from 'src/constants/checklistAccess';

// Roles that can build and edit checklists, and see History. Mirrors
// EDITOR_TYPES in the API, which is what actually enforces it. Buyers (company
// management) only ever see their own sites.
const EDITOR_TYPES = ['admin', 'buyer'];

// How often the Today list refreshes itself, so one person's progress shows up
// on everyone else's phone.
const REFRESH_MS = 60 * 1000;
// How often time-based badges ("Due in 10 min", "Overdue") re-evaluate.
const CLOCK_MS = 30 * 1000;

const Page = styled.div`
  max-width: 90em;
  margin: auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75em;
  margin-bottom: 1em;

  h1 {
    font-family: Nunito, sans-serif;
    font-weight: 800;
    font-size: 22px;
    margin: 0;
    flex: 1 1 auto;
  }
`;

const Checklists = props => {
  const {
    sites = [],
    user = {},
    selectedSite,
    setSelectedSite,
    templates = [],
    library = [],
    runs = [],
    history = [],
    serverTime,
    fetchedAt,
    findChecklistTemplates,
    findChecklistLibrary,
    saveChecklistTemplate,
    deleteChecklistTemplate,
    addChecklistFromLibrary,
    findChecklistRuns,
    findChecklistRun,
    startChecklistRun,
    saveChecklistResponse,
    saveChecklistResponses,
    completeChecklistRun,
    findChecklistHistory,
    assignees = [],
    findChecklistAssignees,
    reassignChecklistRun
  } = props;

  const canEdit = EDITOR_TYPES.includes(user.type);
  // Managers can hand today's checklists to someone else; only admins change
  // the template itself.
  const canAssign = isOversight(user);
  const site = resolveSelectedSite(selectedSite, sites);

  const [activeTab, setActiveTab] = React.useState('today');
  const [templatesLoading, setTemplatesLoading] = React.useState(false);
  const [runsLoading, setRunsLoading] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [editorKey, setEditorKey] = React.useState(0);
  const [openRunId, setOpenRunId] = React.useState(null);
  const [openRunFallback, setOpenRunFallback] = React.useState(null);
  const [historyRange, setHistoryRange] = React.useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 6);
    return { from: localDate(start), to: localDate(end) };
  });
  const [historyLoading, setHistoryLoading] = React.useState(false);
  // Today-tab filters (managers): { person, department, status }.
  const [todayFilters, setTodayFilters] = React.useState({});
  const [, setTick] = React.useState(0);

  // Judge "now" by the server's clock, corrected for time since the last
  // fetch, so a phone set to the wrong time still shows the right badges.
  const skew = serverTime && fetchedAt ? new Date(serverTime).getTime() - fetchedAt : 0;
  const now = React.useCallback(() => new Date(Date.now() + skew), [skew]);

  React.useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), CLOCK_MS);
    return () => clearInterval(id);
  }, []);

  // Editors also see inactive templates; staff only need active ones (for the
  // on-demand "Start" list).
  const loadTemplates = React.useCallback(() => {
    if (!site) return Promise.resolve();
    return findChecklistTemplates(site, canEdit).catch(err =>
      message.error((err && err.message) || 'Could not load checklists.')
    );
  }, [site, canEdit, findChecklistTemplates]);

  const loadRuns = React.useCallback(
    (quiet = false) => {
      if (!site) return Promise.resolve();
      if (!quiet) setRunsLoading(true);
      return findChecklistRuns(site)
        .catch(err => {
          if (!quiet) message.error((err && err.message) || "Could not load today's checklists.");
        })
        .finally(() => setRunsLoading(false));
    },
    [site, findChecklistRuns]
  );

  React.useEffect(() => {
    if (!canEdit) return;
    findChecklistLibrary().catch(() => {
      // The library is optional; the page still works without it.
    });
  }, [canEdit, findChecklistLibrary]);

  React.useEffect(() => {
    setTemplatesLoading(true);
    loadTemplates().finally(() => setTemplatesLoading(false));
    loadRuns();
  }, [loadTemplates, loadRuns]);

  // Another site has other people; don't keep filtering by someone not there.
  React.useEffect(() => setTodayFilters({}), [site]);

  React.useEffect(() => {
    if (!site || !canAssign || !findChecklistAssignees) return;
    findChecklistAssignees(site).catch(() => {
      // Only needed to pick people; everything else still works without it.
    });
  }, [site, canAssign, findChecklistAssignees]);

  const loadHistory = React.useCallback(() => {
    if (!site || !canEdit) return Promise.resolve();
    setHistoryLoading(true);
    return findChecklistHistory(site, historyRange.from, historyRange.to)
      .catch(err => message.error((err && err.message) || 'Could not load history.'))
      .finally(() => setHistoryLoading(false));
  }, [site, canEdit, historyRange, findChecklistHistory]);

  // History is only fetched when someone actually looks at it.
  React.useEffect(() => {
    if (activeTab === 'history' && !openRunId) loadHistory();
  }, [activeTab, openRunId, loadHistory]);

  // Keep the list fresh while someone is looking at it. Paused inside a
  // checklist so a background refresh never fights with what they're typing.
  React.useEffect(() => {
    if (openRunId || editing || (activeTab !== 'today' && activeTab !== 'team')) return undefined;
    const id = setInterval(() => loadRuns(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [openRunId, editing, activeTab, loadRuns]);

  // ---- Templates --------------------------------------------------------

  const openEditor = template => {
    setEditing(template);
    setEditorKey(k => k + 1);
    window.scrollTo(0, 0);
  };

  const departments = Array.from(
    new Set([...DEFAULT_DEPARTMENTS, ...templates.map(t => t.department).filter(Boolean)])
  ).sort();

  const handleSave = async draft => {
    const data = await saveChecklistTemplate({ ...draft, access: draft.access || site });
    await loadTemplates();
    return data && data.template;
  };

  const handleDelete = async id => {
    try {
      await deleteChecklistTemplate(id);
      await loadTemplates();
      setEditing(null);
      message.success('Checklist deleted');
    } catch (err) {
      message.error((err && err.message) || 'Could not delete the checklist.');
    }
  };

  const handleAddFromLibrary = async libraryKey => {
    try {
      const data = await addChecklistFromLibrary(site, libraryKey);
      await loadTemplates();
      message.success(`Added "${data.template.name}"`);
    } catch (err) {
      message.error((err && err.message) || 'Could not add the checklist.');
    }
  };

  const handleDuplicate = template => {
    const { _id, createdAt, updatedAt, libraryKey, ...rest } = template;
    openEditor({ ...rest, name: `${template.name} (copy)`, libraryKey: null });
  };

  // ---- Runs -------------------------------------------------------------

  const openRun = run => {
    setOpenRunFallback(run);
    setOpenRunId(run._id);
    window.scrollTo(0, 0);
    // Pick up anything colleagues ticked since the list loaded.
    findChecklistRun(run._id).catch(() => {});
  };

  const closeRun = () => {
    setOpenRunId(null);
    setOpenRunFallback(null);
    loadRuns(true);
  };

  const handleStart = async (template, fields) => {
    try {
      const data = await startChecklistRun(template._id, fields);
      if (data && data.data && data.data.run) openRun(data.data.run);
      else await loadRuns(true);
    } catch (err) {
      message.error((err && err.message) || 'Could not start the checklist.');
      throw err;
    }
  };

  // A run opened from History may be from another day, so it won't be in
  // today's list — look there too, so its saved answers show up live.
  const activeRun = openRunId
    ? runs.find(r => r._id === openRunId) ||
      history.find(r => r._id === openRunId) ||
      openRunFallback
    : null;

  const siteName = (sites.find(s => String(s._id) === String(site)) || {}).name;

  const tabs = [
    {
      key: 'today',
      label: 'Today',
      children: (
        <TodayView
          runs={runs}
          templates={templates}
          loading={runsLoading}
          now={now}
          onOpen={openRun}
          onStart={handleStart}
          onRefresh={() => loadRuns()}
          canFilter={canAssign}
          filters={todayFilters}
          onFiltersChange={setTodayFilters}
        />
      )
    }
  ];
  if (canAssign) {
    tabs.push({
      key: 'team',
      label: 'Team',
      children: (
        <TeamView
          runs={runs}
          loading={runsLoading}
          now={now}
          onOpen={openRun}
          onRefresh={() => loadRuns()}
          onShowPerson={person => {
            setTodayFilters({ person, status: 'all' });
            setActiveTab('today');
          }}
        />
      )
    });
  }
  if (canEdit) {
    tabs.push({
      key: 'templates',
      label: 'Templates',
      children: (
        <Spin spinning={templatesLoading}>
          <TemplateList
            templates={templates}
            library={library}
            onEdit={openEditor}
            onCreate={() => openEditor(blankTemplate(site))}
            onDuplicate={handleDuplicate}
            onAddFromLibrary={handleAddFromLibrary}
          />
        </Spin>
      )
    });
    tabs.push({
      key: 'history',
      label: 'History',
      children: (
        <HistoryView
          runs={history}
          templates={templates}
          loading={historyLoading}
          from={historyRange.from}
          to={historyRange.to}
          onRangeChange={(from, to) => setHistoryRange({ from, to })}
          now={now}
          today={localDate()}
          onOpen={run => run && openRun(run)}
        />
      )
    });
  }

  if (editing) {
    return (
      <Page>
        <TemplateEditor
          key={editorKey}
          template={editing}
          departments={departments}
          assignees={assignees}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setEditing(null)}
        />
      </Page>
    );
  }

  if (activeRun) {
    return (
      <Page>
        <RunView
          key={activeRun._id}
          run={activeRun}
          user={user}
          now={now}
          onBack={closeRun}
          onSave={saveChecklistResponse}
          onSaveMany={saveChecklistResponses}
          onComplete={completeChecklistRun}
          onRefresh={() => findChecklistRun(activeRun._id).catch(() => {})}
          assignees={assignees}
          onReassign={canAssign ? reassignChecklistRun : null}
        />
      </Page>
    );
  }

  return (
    <Page>
      <Header>
        <h1>Checklists</h1>
        <Select
          size="large"
          showSearch
          optionFilterProp="label"
          value={site || undefined}
          placeholder="Select site"
          onChange={value => setSelectedSite && setSelectedSite(value)}
          options={sites.map(s => ({ value: s._id, label: s.name }))}
          style={{ minWidth: '14em' }}
        />
      </Header>
      {!site ? (
        <p style={{ color: '#5b6660' }}>
          {sites.length ? 'Select a site to see its checklists.' : `Loading ${siteName || 'sites'}…`}
        </p>
      ) : (
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabs} />
      )}
    </Page>
  );
};

export default Checklists;
